import { useMutation } from '@apollo/client'
import { Typography } from '@mui/material'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { CREATE_DRAFT_INVOICE_MUTATION } from '../../../graphql/mutations'
import { useFetchExport, useNotification, useUser } from '../../../utils/hooks'
import { Button } from '../../atoms'

interface DraftInvoiceButtonProps {
  options?: {
    processingId?: string
    onBeforeClick?: (callback: () => void) => void
  }
  disabled?: boolean
  helperText?: string
}

const DraftInvoiceButton: FC<DraftInvoiceButtonProps> = ({
  options: { processingId, onBeforeClick } = {},
  disabled,
  helperText
}) => {
  const { t } = useTranslation('draftInvoiceButton')
  const { setNotification } = useNotification()
  const [loading, setLoading] = useState<boolean>(false)

  const { setDraftInvoiceCallback } = useUser()

  const [createDraftInvoice] = useMutation(CREATE_DRAFT_INVOICE_MUTATION, {
    fetchPolicy: 'network-only',
    variables: {
      processingId
    },
    onCompleted: () => {
      setNotification({
        open: true,
        variant: 'success',
        title: t('success.title'),
        message: t('success.createDraftInvoice')
      })
    },
    onError: error => {
      setNotification({
        open: true,
        variant: 'error',
        title: t('error.title'),
        message: t('error.createDraftInvoice', { error: error.message })
      })
      setLoading(false)
    }
  })
  const headers = {}
  const [{ error, loading: loadingFile }, doFetch] = useFetchExport({
    headers,
    options: { openInNewTab: true }
  })

  useEffect(() => {
    setDraftInvoiceCallback((data: any) => {
      if (data.success) {
        doFetch(data.url)
        setLoading(false)
        return
      }
      setLoading(false)
      setNotification({
        open: true,
        variant: 'error',
        title: t('error.title'),
        message: t('error.createDraftInvoice', { error: data.error })
      })
    })
    return () => {
      setDraftInvoiceCallback((data: any) => {
        console.debug('DraftInvoiceButton', data)
      })
    }
  }, [setDraftInvoiceCallback, doFetch, t, setNotification])

  useEffect(() => {
    if (!loadingFile) setLoading(false)
    if (error) {
      setNotification({
        open: true,
        variant: 'error',
        title: t('error.title'),
        message: t('error.createDraftInvoice', { error: error.message })
      })
    }
  }, [loadingFile, error, t, setNotification])
  const handleDraftInvoice = () => {
    const executeAction = () => {
      setLoading(true)
      createDraftInvoice()
    }

    if (onBeforeClick) {
      onBeforeClick(executeAction)
    } else {
      executeAction()
    }
  }

  return (
    <>
      <Button
        loading={loading}
        loadingPosition="start"
        fullWidth
        onClick={handleDraftInvoice}
        disabled={!processingId || disabled || loading}
      >
        {t('createDraftInvoice')}
      </Button>
      {helperText && (
        <Typography
          variant="caption"
          sx={{ paddingTop: 1, display: 'flex', margin: '0px 14px', width: '100%' }}
        >
          {helperText}
        </Typography>
      )}
    </>
  )
}

export default DraftInvoiceButton
